// installationpop.tsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
    ColumnDef,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import PaginationControls from "components/PaginationControls";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
// ================= Types (self-contained) =================
export type Variant = {
    variant_id: string;
    variant_name: string;
    sku?: string;
    attributes?: Record<string, string>;
    inventory?: Array<{ available?: number }>; // minimal for available sum
};

export type Product = {
    name?: string;
    sku?: string;
    category?: string;
    images?: Array<{ url: string }>;
    image?: { url?: string };
    variants?: Variant[];
    current_status?: string;
    product_location_status?: string;
};

export type ProductRow = {
    product_id: string;
    name: string;
    sku: string;
    category: string;
    product: Product;
    inventory_type: "agent" | "office" | string;
    inventory_source: string
    current_status?: string;
    images: Array<{ url: string }>;
    agent_id?: string;
    office_id?: string;
    product_current_status?: string;
    available_quantity?: number; // Add this new field
    location?: Array<{
        quantity: number;
        warehouse_id: string;
        warehouse_name?: string;
        zone_id?: string;
        zone_name?: string;
        rack_id?: string;
        rack_name?: string;
        bin_id?: string;
        bin_name?: string;
    }>;
};

// ================= Props =================
interface Props {
    open: boolean;
    onClose: () => void;
    userType: "agent" | "office";
    userId: string;
    signageCompanyId: string;
    token: string;
    onAdd: (flattenedProduct: any) => void; // parent expects a flattened row payload
}

const columnHelper = createColumnHelper<ProductRow>();
type PaginationState = { pageIndex: number; pageSize: number };
const HEX24 = /^[a-f0-9]{24}$/i;
const ABS_URL = /^https?:\/\//i;
const isHexId = (s?: string) => !!(s && HEX24.test(s.trim()));
// ================= Component =================
const RealtorProductPicker: React.FC<Props> = ({
    open,
    onClose,
    userType,
    userId,
    signageCompanyId,
    token,
    onAdd,
}) => {
    // Datatable state
    const [rows, setRows] = useState<ProductRow[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
    const [pageSize, setPageSize] = useState<number>(10);
    const [globalFilter, setGlobalFilter] = useState("");
    const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
    const apiUrl = `${process.env.REACT_APP_API_URL}/datatable/incoming/${userType}/${userId}/organizations/${signageCompanyId}`;
 console.log("rowdafwsgh",rows)
    const allImageIds = useMemo(() => {
        const ids: string[] = [];
        for (const row of (rows || [])) {
            const id = row?.images?.[0]?.url as string;
            if (isHexId(id)) ids.push(id.trim());
        }
        return Array.from(new Set(ids));
    }, [rows]);

    console.log("allImageIds", allImageIds)
    // hook expects: [{ product: { variants: [{ images: [{ url }] }] } }]   
    const dataForMap = useMemo(() => {
        if (allImageIds.length === 0) return [];
        return [
            { product: { variants: [{ images: allImageIds.map(url => ({ url })) }] } }
        ];
    }, [allImageIds]);

    const imageUrlMap = useVariantImageUrlMap(dataForMap, token);
    // Fetch data
    useEffect(() => {
        if (!open) return;
        const controller = new AbortController();
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        page: pagination.pageIndex + 1,
                        limit: pagination.pageSize,
                        search: globalFilter,
                        filters: {},
                        sort: { field: "created_at", order: "desc" },
                    }),
                    signal: controller.signal,
                });
                const json = await res.json();
                if (!res.ok) throw new Error(json?.error || "Failed to fetch inventory");
                const data: ProductRow[] = Array.isArray(json?.data)
                    ? json.data
                    : Array.isArray(json)
                        ? (json as any)
                        : [];
                console.log("data", data);
                setRows(data);
                setTotal(Number(json?.total ?? data.length));
            } catch (e: any) {
                if (e.name === "AbortError") return;
                setError(e?.message || "Unknown error");
            } finally {
                setLoading(false);
            }
        })();
        return () => controller.abort();
    }, [open, apiUrl, token, pagination.pageIndex, pagination.pageSize, globalFilter]);

    const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));

    // Build flattened payload on Add
    const buildFlattened = (row: ProductRow) => {
        const p = row?.product || ({} as Product);
        const variant = Array.isArray(p?.variants) ? p.variants[0] : undefined;
        const inventory = Array.isArray(variant?.inventory) ? variant!.inventory! : [];
        // Use the new available_quantity field from backend, fallback to calculated value
        const totalAvailableQty = row.available_quantity ?? inventory.reduce((sum, loc) => sum + (Number(loc?.available) || 0), 0);
        // console.log("totalAvailableQty", totalAvailableQty);
        // console.log("row1", row);
        // console.log("inventory", inventory);

        return {
            product_id: row.product_id,
            name: row?.name,
            sku: row.sku || variant?.sku || "",
            inventory_type: row?.inventory_source,
            product_location_status: p.product_location_status,
            user_id: userId,
            current_status: row.product_current_status,
            product_current_status: row.current_status,
            availableqty: totalAvailableQty,
            quantity: 1,
            updateStatus: "",
            productionStatus: "",
            image_url:
                (Array.isArray(p?.images) && p.images.length > 0 && p.images[0]?.url) ||
                p?.image?.url ||
                "",
            location: row.location || [],
        };
    };
    // console.log("imagw",imageUrlMap)    

    // Columns
    const columns = useMemo<ColumnDef<ProductRow, any>[]>(() => [
        columnHelper.display({
            id: "image",
            header: () => <div className="text-start">Image</div>,
            cell: (info) => {
                const raw = (info.row.original as any)?.images?.[0]?.url as string | undefined;
                let url = "";

                if (raw) {
                    if (ABS_URL.test(raw)) {
                        // already a full URL
                        url = raw;
                    } else if (isHexId(raw)) {
                        // hex id -> resolve via presigned map / helper
                        const map: Record<string, string> = (imageUrlMap as any) || {};
                        url = map[raw] || getImageSrc(raw, map) || "";
                    } else {
                        // legacy relative path
                        const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
                        const path = raw.replace(/^\/+/, "");
                        url = `${base}/${encodeURI(path)}`;
                    }
                }

                return (
                    <div className="text-sm">
                        {url ? (
                            <img src={url} alt="Product" style={{ width: 60, height: 60, objectFit: "contain", borderRadius: 4 }} />
                        ) : (
                            <span>No Image</span>
                        )}
                    </div>
                );
            },
        }),
        columnHelper.accessor((r) => r?.name || "-", {
            id: "name",
            header: () => <div className="w-100 text-start">Product Name</div>,
            cell: (info) => {
                const row = info.row.original;
                const p = row.product;
                return (
                    <div className="td-name title-sm">
                        <div className="fw-semibold">{info.getValue()}</div>
                        <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                            SKU: {row?.sku || p?.variants?.[0]?.sku || "-"}
                        </div>
                    </div>
                );
            },
        }),
        columnHelper.accessor((r) => r?.category || "-", {
            id: "category",
            header: () => <div className="text-start">Category</div>,
            cell: (info) => <div className="ins-category-badge">{info.getValue()}</div>,
        }),
        columnHelper.accessor((r) => r?.available_quantity ?? 0, {
            id: "available_quantity",
            header: () => <div className="text-start">Avl Qty</div>,
            cell: (info) => (
                <div className="td-available-qty">
                    {info.getValue()}
                </div>
            ),
        }),
        columnHelper.display({
            id: "action",
            header: () => <div className="text-start">Action</div>,
            cell: (info) => (
                <div className="td-action">
                    <button
                        type="button"
                        className="pp-add-custom-btn"
                        onClick={() => onAdd(buildFlattened(info.row.original))}
                        disabled={!info.row.original.available_quantity || info.row.original.available_quantity <= 0}
                    >
                        Add
                    </button>
                </div>
            ),
        }),
    ], [pagination, pageSize, onAdd,imageUrlMap]);


    const table = useReactTable({
        data: rows,
        columns,
        pageCount: Math.max(1, Math.ceil(total / pagination.pageSize)),
        state: { pagination, globalFilter },
        manualPagination: true,
        manualFiltering: true,
        onPaginationChange: setPagination,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
    });

    if (!open) return null;
return createPortal(
  <div style={{ position: "fixed", inset: 0, zIndex: 1060 }}>
    {/* Backdrop */}
    <div
      className="modal-backdrop fade show"
      style={{ position: "fixed", inset: 0 }}
      onClick={onClose}
    />

    {/* Modal */}
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,.35)" }}
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content ins-modal" id="installationoutpopup">
          {/* ================= HEADER (HTML STYLE) ================= */}
          <div className="ins-header">
            <div className="ins-header-top">
              <h2 className="ins-title">Select a product</h2>

              <button
                type="button"
                className="ins-close-btn"
                aria-label="Close"
                onClick={onClose}
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="ins-header-search">
              <div className="ins-search-box">
                <i className="ri-search-line ins-search-icon" />
                <input
                  type="text"
                  className="ins-search-input"
                  placeholder="Search by product name or SKU"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setGlobalFilter(searchInput);
                  }}
                />
              </div>
            </div>
          </div>

          {/* ================= BODY ================= */}
          <div className="modal-body p-0">
            {loading && <div className="text-center py-5">Loading…</div>}
            {error && <div className="text-danger p-3">{error}</div>}

          {!loading && !error && (
  <>
    {/* ✅ Desktop table (>=1022px) */}
    <div className="ins-desktop">
      <div className="ins-table-container">
        <table className="ins-table" style={{ minWidth: 720 }}>
          <colgroup>
            <col style={{ width: 120 }} />
            <col />
            <col style={{ width: 140 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 160 }} />
          </colgroup>

          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id} className="text-start">
                    {h.isPlaceholder
                      ? null
                      : flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell, idx) => (
                  <td
                    key={cell.id}
                    className={idx === 1 ? "text-start ins-td-name" : "text-start"}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* ✅ Mobile cards (<=1021px) */}
    <div className="ins-mobile">
      <div className="ins-cards-wrap">
        {table.getRowModel().rows.map((row) => {
          const cells = row.getVisibleCells();

          // ✅ Prefer column ids; fallback to index
          const imageCell =
            cells.find((c) => c.column.id === "image") || cells[0];
          const nameCell =
            cells.find((c) => c.column.id === "name" || c.column.id === "product_name") ||
            cells[1];
          const categoryCell =
            cells.find((c) => c.column.id === "category") || cells[2];
          const qtyCell =
            cells.find((c) => c.column.id === "avl_qty" || c.column.id === "qty") ||
            cells[3];
          const actionCell =
            cells.find((c) => c.column.id === "action" || c.column.id === "actions") ||
            cells[cells.length - 1];

          return (
            <div key={row.id} className="ins-card">
              <div className="ins-card-top">
                <div className="ins-card-img">
                  {imageCell &&
                    flexRender(imageCell.column.columnDef.cell, imageCell.getContext())}
                </div>

                <div className="ins-card-main">
                  {nameCell &&
                    flexRender(nameCell.column.columnDef.cell, nameCell.getContext())}

                  <div className="ins-card-meta">
                    <div className="ins-card-meta-item">
                      <div className="ins-card-label">Category</div>

                      {/* ✅ IMPORTANT: render the SAME table cell so your badge shows */}
                      <div className="ins-card-value">
                        {categoryCell &&
                          flexRender(
                            categoryCell.column.columnDef.cell,
                            categoryCell.getContext()
                          )}
                      </div>
                    </div>

                    <div className="ins-card-meta-item">
                      <div className="ins-card-label">Avl Qty</div>
                      <div className="ins-card-value">
                        {qtyCell &&
                          flexRender(qtyCell.column.columnDef.cell, qtyCell.getContext())}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ins-card-action">
                  {actionCell &&
                    flexRender(actionCell.column.columnDef.cell, actionCell.getContext())}
                </div>
              </div>
            </div>
          );
        })}

        {table.getRowModel().rows.length === 0 && (
          <div className="text-center py-4 text-muted">No records found</div>
        )}
      </div>
    </div>
  </>
)}

          </div>

          {/* ================= FOOTER (HTML STYLE) ================= */}
         {/* footer pagination */}
                {table.getRowModel().rows.length === 0 ? (
                  <div className="text-center my-4 text-muted" style={{ fontSize: "1.05rem" }}>
                    No records found
                  </div>
                ) : (
                  <div className="px-4 pb-4 pt-2 border-top bg-white">
                    <PaginationControls table={table} />
                  </div>
                )}
        </div>
      </div>
    </div>

    {/* ================= STYLES (MATCH HTML) ================= */}
    <style>{`
      /* Modal shell */
      .ins-modal{
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 24px 48px rgba(0,0,0,0.2);
        animation: insModalSlideIn .3s ease-out;
      }
      @keyframes insModalSlideIn {
        from { opacity: 0; transform: translateY(30px) scale(.98); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
      }

      /* Header */
      .ins-header{
        padding: 28px 32px;
        border-bottom: 1px solid #e5e7eb;
        background: linear-gradient(to bottom, #ffffff, #fafbfc);
        display: flex;
        flex-direction: column;
        gap: 20px;
        position: sticky;
        top: 0;
        z-index: 20;
      }
      .ins-header-top{
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      }
      .ins-title{
        font-size: 22px;
        font-weight: 600;
        color: #111827;
        letter-spacing: -0.02em;
        margin: 0;
      }
      .ins-close-btn{
        position: absolute;
        right: 0;
        width: 36px;
        height: 36px;
        border-radius: 8px;
        border: none;
        background: #f3f4f6;
        color: #6b7280;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all .2s;
      }
      .ins-close-btn i{ font-size: 20px; line-height: 1; }
      .ins-close-btn:hover{
        background: #fee2e2;
        color: #dc2626;
        transform: scale(1.05);
      }

      .ins-header-search{
        display: flex;
        justify-content: center;
      }
      .ins-search-box{
        position: relative;
        width: 100%;
        max-width: 500px;
      }
      .ins-search-icon{
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: #9ca3af;
        pointer-events: none;
        font-size: 18px;
      }
      .ins-search-input{
        width: 100%;
        padding: 12px 20px 12px 46px;
        border: 1.5px solid #d1d5db;
        border-radius: 10px;
        font-size: 15px;
        transition: all .2s;
        background: #fff;
      }
      .ins-search-input::placeholder{ color: #9ca3af; }
      .ins-search-input:focus{
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 4px rgba(59,130,246,0.1);
      }

      /* Table container + scrollbar */
      .ins-table-container{
        max-height: 420px;
        overflow-y: auto;
        overflow-x: auto;
      }
      .ins-table-container::-webkit-scrollbar{ width: 10px; height: 10px; }
      .ins-table-container::-webkit-scrollbar-track{ background: #f3f4f6; }
      .ins-table-container::-webkit-scrollbar-thumb{ background: #d1d5db; border-radius: 5px; }
      .ins-table-container::-webkit-scrollbar-thumb:hover{ background: #9ca3af; }

      /* Table */
      .ins-table{
        width: 100%;
        border-collapse: collapse;
      }
      .ins-table thead{
        position: sticky;
        top: 0;
        z-index: 10;
        background: #fff;
      }
      .ins-table thead tr{
        border-bottom: 2px solid #e5e7eb;
      }
      .ins-table th{
        padding: 16px 24px;
        text-align: left;
        font-size: 13px;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        background: #f9fafb;
        white-space: nowrap;
      }
      .ins-table td{
        padding: 24px 24px;
        font-size: 14px;
        color: #374151;
        vertical-align: middle;
      }
      .ins-table tbody tr{
        border-bottom: 1px solid #f3f4f6;
        transition: all .2s;
      }
      .ins-table tbody tr:hover{
        background: #fafbfc;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      }

      /* last column right align like HTML */
      .ins-table th:last-child,
      .ins-table td:last-child{
        text-align: right;
        padding-right: 32px;
      }
      .ins-table th:first-child,
      .ins-table td:first-child{
        padding-left: 32px;
      }

      /* Product name column can wrap nicely */
      .ins-td-name{
        max-width: 400px;
        white-space: normal;
      }

      /* Footer */
      .ins-footer{
        padding: 16px 32px;
        border-top: 1px solid #e5e7eb;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 24px;
        background: #fafbfc;
        position: sticky;
        bottom: 0;
        z-index: 20;
      }
        /* Category pill like your 1st image */
.ins-category-badge{
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #166534;
  border: 1px solid #bbf7d0;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  letter-spacing: 0.01em;
  white-space: nowrap;
}

/* optional: if category comes lowercase */
.ins-category-badge{
  text-transform: capitalize;
}
        .pp-add-custom-btn{
        padding:10px 28px;
        background:linear-gradient(135deg,#3b82f6 0%,#2563eb 100%);
        color:#fff;
        border:none;
        border-radius:8px;
        font-size:14px;
        font-weight:600;
        cursor:pointer;
        transition:all .2s;
        box-shadow:0 2px 4px rgba(59,130,246,0.2);
      }
      .pp-add-custom-btn:hover{
        background:linear-gradient(135deg,#2563eb 0%,#1d4ed8 100%);
        box-shadow:0 4px 12px rgba(59,130,246,0.35);
        transform:translateY(-2px);
      }
        /* ✅ Desktop vs Mobile switch */
.ins-desktop { display: block; }
.ins-mobile { display: none; }

@media (max-width: 1021px){
  .ins-desktop { display: none; }
  .ins-mobile { display: block; }
}

/* ✅ Mobile cards UI */
.ins-cards-wrap{
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 420px;
  overflow-y: auto;
}

.ins-card{
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  padding: 12px;
}

.ins-card-top{
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.ins-card-img{
  width: 74px;
  flex: 0 0 74px;
}

/* if your image cell renders an <img>, make it look nice */
.ins-card-img img{
  width: 74px;
  height: 74px;
  object-fit: cover;
  border-radius: 10px;
  display: block;
}

.ins-card-main{
  flex: 1;
  min-width: 0;
}

.ins-card-meta{
  margin-top: 10px;
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.ins-card-meta-item{
  min-width: 130px;
}

.ins-card-label{
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.ins-card-value{
  font-size: 14px;
  color: #111827;
}

.ins-card-action{
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
  .title-sm{
font-size: 1.05rem;
// color: "#364153";
    font-family: "Roboto", sans-serif;
}



    `}</style>
  </div>,
  document.body
);

    // Use a portal + fixed positioning so it appears over any scroll container
//     return createPortal(
//         <div style={{ position: "fixed", inset: 0, zIndex: 1060 }}>
//             {/* Backdrop */}
//             <div
//                 className="modal-backdrop fade show"
//                 style={{ position: "fixed", inset: 0 }}
//                 onClick={onClose}
//             />

//             {/* Modal */}
//             <div
//                 className="modal fade show"
//                 style={{ display: "block", backgroundColor: "rgba(0,0,0,.35)" }}
//                 aria-modal="true"
//                 role="dialog"
//             >
//                 <div className="modal-dialog modal-xl modal-dialog-centered">
//                     <div className="modal-content" id="installationoutpopup">
//                         {/* ==== STICKY HEADER: title + search + close ==== */}
//                         <div className="modal-header flex-wrap gap-2 sticky-top bg-white mb-3">
//                             <h5 className="modal-title me-auto">Select a Product</h5>

//                             {/* Search */}
//                             <div className="input-group border br-8 search-group order-3 order-md-2">
//                                 <span className="input-group-text bg-transparent border-0 ps-3 pe-1">
//                                     <i className="ri-search-line text-muted" />
//                                 </span>
//                                 <input
//                                     type="text"
//                                     className="form-control border-0 shadow-none ps-1 br-8"
//                                     placeholder="Search by Product Name or SKU"
//                                     value={searchInput}
//                                     onChange={(e) => setSearchInput(e.target.value)}
//                                     onKeyDown={(e) => {
//                                         if (e.key === "Enter") setGlobalFilter(searchInput);
//                                     }}
//                                 />
//                                 {searchInput && (
//                                     <button
//                                         type="button"
//                                         className="btn btn-link text-muted border-0 pe-3"
//                                         onClick={() => {
//                                             setSearchInput("");
//                                             setGlobalFilter("");
//                                         }}
//                                     >
//                                         <i className="ri-close-circle-line" />
//                                     </button>
//                                 )}
//                             </div>

//                             {/* Close */}
//                             <button
//                                 type="button"
//                                 className="btn-close order-2 order-md-3 ms-md-2"
//                                 aria-label="Close"
//                                 onClick={onClose}
//                             />
//                         </div>

//                         {/* ==== SCROLLING BODY ==== */}
//                         <div className="modal-body p-0">
//                             {loading && <div className="text-center py-5">Loading…</div>}
//                             {error && <div className="text-danger p-3">{error}</div>}
// {!loading && !error && (
//   <>
//     {/* ✅ Desktop table (>=1022px) */}
//     <div className="ins-desktop">
//       <div className="ins-table-container">
//         <table className="ins-table" style={{ minWidth: 720 }}>
//           <colgroup>
//             <col style={{ width: 120 }} />
//             <col />
//             <col style={{ width: 160 }} />
//             <col style={{ width: 120 }} />
//             <col style={{ width: 160 }} />
//           </colgroup>

//           <thead>
//             {table.getHeaderGroups().map((hg) => (
//               <tr key={hg.id}>
//                 {hg.headers.map((h) => (
//                   <th key={h.id} className="text-start">
//                     {h.isPlaceholder
//                       ? null
//                       : flexRender(h.column.columnDef.header, h.getContext())}
//                   </th>
//                 ))}
//               </tr>
//             ))}
//           </thead>

//           <tbody>
//             {table.getRowModel().rows.map((row) => (
//               <tr key={row.id}>
//                 {row.getVisibleCells().map((cell, idx) => (
//                   <td
//                     key={cell.id}
//                     className={idx === 1 ? "text-start ins-td-name" : "text-start"}
//                   >
//                     {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                   </td>
//                 ))}
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     </div>

//     {/* ✅ Mobile cards (<=1021px) */}
//     <div className="ins-mobile">
//       <div className="ins-cards-wrap">
//         {table.getRowModel().rows.map((row) => {
//           const cells = row.getVisibleCells();

//           // ✅ Prefer column ids; fallback to index
//           const imageCell =
//             cells.find((c) => c.column.id === "image") || cells[0];
//           const nameCell =
//             cells.find((c) => c.column.id === "name" || c.column.id === "product_name") ||
//             cells[1];
//           const categoryCell =
//             cells.find((c) => c.column.id === "category") || cells[2];
//           const qtyCell =
//             cells.find((c) => c.column.id === "avl_qty" || c.column.id === "qty") ||
//             cells[3];
//           const actionCell =
//             cells.find((c) => c.column.id === "action" || c.column.id === "actions") ||
//             cells[cells.length - 1];

//           return (
//             <div key={row.id} className="ins-card">
//               <div className="ins-card-top">
//                 <div className="ins-card-img">
//                   {imageCell &&
//                     flexRender(imageCell.column.columnDef.cell, imageCell.getContext())}
//                 </div>

//                 <div className="ins-card-main">
//                   {nameCell &&
//                     flexRender(nameCell.column.columnDef.cell, nameCell.getContext())}

//                   <div className="ins-card-meta">
//                     <div className="ins-card-meta-item">
//                       <div className="ins-card-label">Category</div>

//                       {/* ✅ IMPORTANT: render the SAME table cell so your badge shows */}
//                       <div className="ins-card-value">
//                         {categoryCell &&
//                           flexRender(
//                             categoryCell.column.columnDef.cell,
//                             categoryCell.getContext()
//                           )}
//                       </div>
//                     </div>

//                     <div className="ins-card-meta-item">
//                       <div className="ins-card-label">Avl Qty</div>
//                       <div className="ins-card-value">
//                         {qtyCell &&
//                           flexRender(qtyCell.column.columnDef.cell, qtyCell.getContext())}
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="ins-card-action">
//                   {actionCell &&
//                     flexRender(actionCell.column.columnDef.cell, actionCell.getContext())}
//                 </div>
//               </div>
//             </div>
//           );
//         })}

//         {table.getRowModel().rows.length === 0 && (
//           <div className="text-center py-4 text-muted">No records found</div>
//         )}
//       </div>
//     </div>
//   </>
// )}

//                         </div>

//                         {/* ==== STICKY FOOTER: pagination ==== */}
//                         {table.getRowModel().rows.length === 0 ? (
//                             <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
//                                 No records found
//                             </div>
//                         ) : (
//                             <div className="modal-footer sticky-bottom bg-white justify-content-center flex-wrap gap-2">
//                                 <PaginationControls table={table} />
//                             </div>
//                         )}
//                     </div>
//                 </div>
//             </div>
//         </div>,
//         document.body
//     );


};

export default RealtorProductPicker;












//  Use a portal + fixed positioning so it appears over any scroll container
// return createPortal(
//   <div style={{ position: "fixed", inset: 0, zIndex: 1060 }}>
//     {/* Backdrop */}
//     <div
//       className="modal-backdrop fade show"
//       style={{ position: "fixed", inset: 0 }}
//       onClick={onClose}
//     />

//     {/* Modal */}
//     <div
//       className="modal fade show"
//       style={{ display: "block", backgroundColor: "rgba(0,0,0,.35)" }}
//       aria-modal="true"
//       role="dialog"
//     >
//       <div className="modal-dialog modal-xl modal-dialog-centered">
//         <div className="modal-content ins-modal" id="installationoutpopup">
//           {/* ================= HEADER (HTML STYLE) ================= */}
//           <div className="ins-header">
//             <div className="ins-header-top">
//               <h2 className="ins-title">Select a product</h2>

//               <button
//                 type="button"
//                 className="ins-close-btn"
//                 aria-label="Close"
//                 onClick={onClose}
//               >
//                 <i className="ri-close-line" />
//               </button>
//             </div>

//             <div className="ins-header-search">
//               <div className="ins-search-box">
//                 <i className="ri-search-line ins-search-icon" />
//                 <input
//                   type="text"
//                   className="ins-search-input"
//                   placeholder="Search by product name or SKU"
//                   value={searchInput}
//                   onChange={(e) => setSearchInput(e.target.value)}
//                   onKeyDown={(e) => {
//                     if (e.key === "Enter") setGlobalFilter(searchInput);
//                   }}
//                 />
//               </div>
//             </div>
//           </div>

//           {/* ================= BODY ================= */}
//           <div className="modal-body p-0">
//             {loading && <div className="text-center py-5">Loading…</div>}
//             {error && <div className="text-danger p-3">{error}</div>}

//             {!loading && !error && (
//               <div className="ins-table-container">
//                 <table className="ins-table" style={{ minWidth: 720 }}>
//                   <colgroup>
//                     <col style={{ width: 120 }} />
//                     <col />
//                     <col style={{ width: 160 }} />
//                     <col style={{ width: 120 }} />
//                     <col style={{ width: 160 }} />
//                   </colgroup>

//                   <thead>
//                     {table.getHeaderGroups().map((hg) => (
//                       <tr key={hg.id}>
//                         {hg.headers.map((h) => (
//                           <th key={h.id} className="text-start">
//                             {h.isPlaceholder
//                               ? null
//                               : flexRender(h.column.columnDef.header, h.getContext())}
//                           </th>
//                         ))}
//                       </tr>
//                     ))}
//                   </thead>

//                   <tbody>
//                     {table.getRowModel().rows.map((row) => (
//                       <tr key={row.id}>
//                         {row.getVisibleCells().map((cell, idx) => (
//                           <td
//                             key={cell.id}
//                             className={idx === 1 ? "text-start ins-td-name" : "text-start"}
//                           >
//                             {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                           </td>
//                         ))}
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             )}
//           </div>

//           {/* ================= FOOTER (HTML STYLE) ================= */}
//          {/* footer pagination */}
//                 {table.getRowModel().rows.length === 0 ? (
//                   <div className="text-center my-4 text-muted" style={{ fontSize: "1.05rem" }}>
//                     No records found
//                   </div>
//                 ) : (
//                   <div className="px-4 pb-4 pt-2 border-top bg-white">
//                     <PaginationControls table={table} />
//                   </div>
//                 )}
//         </div>
//       </div>
//     </div>

//     {/* ================= STYLES (MATCH HTML) ================= */}
//     <style>{`
//       /* Modal shell */
//       .ins-modal{
//         border-radius: 12px;
//         overflow: hidden;
//         box-shadow: 0 24px 48px rgba(0,0,0,0.2);
//         animation: insModalSlideIn .3s ease-out;
//       }
//       @keyframes insModalSlideIn {
//         from { opacity: 0; transform: translateY(30px) scale(.98); }
//         to   { opacity: 1; transform: translateY(0) scale(1); }
//       }

//       /* Header */
//       .ins-header{
//         padding: 28px 32px;
//         border-bottom: 1px solid #e5e7eb;
//         background: linear-gradient(to bottom, #ffffff, #fafbfc);
//         display: flex;
//         flex-direction: column;
//         gap: 20px;
//         position: sticky;
//         top: 0;
//         z-index: 20;
//       }
//       .ins-header-top{
//         display: flex;
//         align-items: center;
//         justify-content: center;
//         position: relative;
//       }
//       .ins-title{
//         font-size: 22px;
//         font-weight: 600;
//         color: #111827;
//         letter-spacing: -0.02em;
//         margin: 0;
//       }
//       .ins-close-btn{
//         position: absolute;
//         right: 0;
//         width: 36px;
//         height: 36px;
//         border-radius: 8px;
//         border: none;
//         background: #f3f4f6;
//         color: #6b7280;
//         cursor: pointer;
//         display: flex;
//         align-items: center;
//         justify-content: center;
//         transition: all .2s;
//       }
//       .ins-close-btn i{ font-size: 20px; line-height: 1; }
//       .ins-close-btn:hover{
//         background: #fee2e2;
//         color: #dc2626;
//         transform: scale(1.05);
//       }

//       .ins-header-search{
//         display: flex;
//         justify-content: center;
//       }
//       .ins-search-box{
//         position: relative;
//         width: 100%;
//         max-width: 500px;
//       }
//       .ins-search-icon{
//         position: absolute;
//         left: 16px;
//         top: 50%;
//         transform: translateY(-50%);
//         color: #9ca3af;
//         pointer-events: none;
//         font-size: 18px;
//       }
//       .ins-search-input{
//         width: 100%;
//         padding: 12px 20px 12px 46px;
//         border: 1.5px solid #d1d5db;
//         border-radius: 10px;
//         font-size: 15px;
//         transition: all .2s;
//         background: #fff;
//       }
//       .ins-search-input::placeholder{ color: #9ca3af; }
//       .ins-search-input:focus{
//         outline: none;
//         border-color: #3b82f6;
//         box-shadow: 0 0 0 4px rgba(59,130,246,0.1);
//       }

//       /* Table container + scrollbar */
//       .ins-table-container{
//         max-height: 420px;
//         overflow-y: auto;
//         overflow-x: auto;
//       }
//       .ins-table-container::-webkit-scrollbar{ width: 10px; height: 10px; }
//       .ins-table-container::-webkit-scrollbar-track{ background: #f3f4f6; }
//       .ins-table-container::-webkit-scrollbar-thumb{ background: #d1d5db; border-radius: 5px; }
//       .ins-table-container::-webkit-scrollbar-thumb:hover{ background: #9ca3af; }

//       /* Table */
//       .ins-table{
//         width: 100%;
//         border-collapse: collapse;
//       }
//       .ins-table thead{
//         position: sticky;
//         top: 0;
//         z-index: 10;
//         background: #fff;
//       }
//       .ins-table thead tr{
//         border-bottom: 2px solid #e5e7eb;
//       }
//       .ins-table th{
//         padding: 16px 24px;
//         text-align: left;
//         font-size: 13px;
//         font-weight: 600;
//         color: #6b7280;
//         text-transform: uppercase;
//         letter-spacing: 0.05em;
//         background: #f9fafb;
//         white-space: nowrap;
//       }
//       .ins-table td{
//         padding: 24px 24px;
//         font-size: 14px;
//         color: #374151;
//         vertical-align: middle;
//       }
//       .ins-table tbody tr{
//         border-bottom: 1px solid #f3f4f6;
//         transition: all .2s;
//       }
//       .ins-table tbody tr:hover{
//         background: #fafbfc;
//         box-shadow: 0 2px 8px rgba(0,0,0,0.04);
//       }

//       /* last column right align like HTML */
//       .ins-table th:last-child,
//       .ins-table td:last-child{
//         text-align: right;
//         padding-right: 32px;
//       }
//       .ins-table th:first-child,
//       .ins-table td:first-child{
//         padding-left: 32px;
//       }

//       /* Product name column can wrap nicely */
//       .ins-td-name{
//         max-width: 400px;
//         white-space: normal;
//       }

//       /* Footer */
//       .ins-footer{
//         padding: 16px 32px;
//         border-top: 1px solid #e5e7eb;
//         display: flex;
//         justify-content: flex-end;
//         align-items: center;
//         gap: 24px;
//         background: #fafbfc;
//         position: sticky;
//         bottom: 0;
//         z-index: 20;
//       }
//         /* Category pill like your 1st image */
// .ins-category-badge{
//   display: inline-flex;
//   align-items: center;
//   padding: 7px 14px;
//   border-radius: 8px;
//   font-size: 14px;
//   font-weight: 600;
//   color: #166534;
//   border: 1px solid #bbf7d0;
//   background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
//   letter-spacing: 0.01em;
//   white-space: nowrap;
// }

// /* optional: if category comes lowercase */
// .ins-category-badge{
//   text-transform: capitalize;
// }
//         .pp-add-custom-btn{
//         padding:10px 28px;
//         background:linear-gradient(135deg,#3b82f6 0%,#2563eb 100%);
//         color:#fff;
//         border:none;
//         border-radius:8px;
//         font-size:14px;
//         font-weight:600;
//         cursor:pointer;
//         transition:all .2s;
//         box-shadow:0 2px 4px rgba(59,130,246,0.2);
//       }
//       .pp-add-custom-btn:hover{
//         background:linear-gradient(135deg,#2563eb 0%,#1d4ed8 100%);
//         box-shadow:0 4px 12px rgba(59,130,246,0.35);
//         transform:translateY(-2px);
//       }

//     `}</style>
//   </div>,
//   document.body
// );